import type { Theme } from '@mui/material';
import type { IProductItem, IEventItemProps, IProductFilters } from 'src/types/product';

import { useState } from 'react';
import { orderBy } from 'es-toolkit';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useMediaQuery } from '@mui/material';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';

import { PRODUCT_SORT_OPTIONS } from 'src/_mock';

import { EmptyContent } from 'src/components/empty-content';

import { ProductList } from '../product-list';
import { ProductSort } from '../product-sort';
import ActivitiesMap from '../product-list-on-map';
import { ProductFiltersMenu } from '../product-filters-menu';

// ----------------------------------------------------------------------
type Props = {
  products: IEventItemProps[];
  productsCount: number;
  loading?: boolean;
  categoryName: string;
  setCategoryName: (name: string) => void;
};

export function ProductShopView({
  products,
  productsCount,
  loading,
  categoryName,
  setCategoryName,
}: Props) {
  const downSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [sortBy, setSortBy] = useState('createdAtDescWithPriority');
  const [showMap, setShowMap] = useState(false);


  const renderFilters = () => (
    <Box
      sx={{
        gap: 3,
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-end', sm: 'center' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignContent: 'flex-end',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography
          variant="h4"
          fontFamily="Rubik"
          fontWeight={500}
          sx={{ mt: { xs: 3, md: 4.5 } }}
        >
          {categoryName}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            flexDirection: { xs: 'column', sm: 'row-reverse' },
            gap: { xs: 1, sm: 2 },
          }}
        >
          <ProductSort
            sort={sortBy}
            onSort={(newValue: string) => setSortBy(newValue)}
            sortOptions={PRODUCT_SORT_OPTIONS}
            setShowMap={setShowMap}
            showMap={showMap}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Container sx={{ mb: 15 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: downSm ? '1fr' : '282px 1fr',
          gap: 6,
        }}
      >
        <ProductFiltersMenu sort={sortBy} setCategoryName={setCategoryName} showMap={showMap} />

        <Box>
          <Stack spacing={2.5} sx={{ mb: { xs: 3, md: 3 } }}>
            {renderFilters()}
          </Stack>

          {!showMap ? (
            <ProductList
              products={products ?? []}
              productsCount={productsCount}
              loading={loading}
            />
          ) : (
            <ActivitiesMap activities={products} />
          )}
        </Box>
      </Box>
    </Container>
  );
}
